import 'ol/ol.css';
import * as helper from './helper';
import XYZ from 'ol/source/XYZ';
import Point from 'ol/geom/Point';
import { Vector } from 'ol/layer';
import { fromLonLat } from 'ol/proj';
import { Coordinate } from 'ol/coordinate';
import { Map as OlMap, Overlay, View } from 'ol';
import { POPUP_ACTIVE, POPUP_CONTENT, POPUP_ID } from './feature-keys';

export interface MarkerOption {
  point: [number, number] | Coordinate | Point;
  image: string;
  popup?: string;
  onClick?: () => void;
}

export interface MapOptions {
  id: string;
  attrs?: string;
  markers?: MarkerOption[];
  url?: string;
  sourceClass?: typeof XYZ;
  center?: [number, number] | Coordinate;
  zoom?: number;
  maxZoom?: number;
  minZoom?: number;
  markerFitZoom?: boolean;
  markerFitZoomPadding?: number;
  markerPopupOnClick?: (e: React.MouseEvent) => void;
}

export class Map {

  protected map: OlMap = null;

  private static featureIndex = 0;

  constructor(options: MapOptions) {

    const markers = this.makeMarkers(options.markers ?? []);

    this.map = new OlMap({
      target: options.id,
      layers: [
        helper.createTileLayer({
          url: options.url,
          source: options.sourceClass,
          attrs: options.attrs
        }),
        ...markers.layers
      ],
      view: new View({
        center: fromLonLat(options.center ?? [0, 0]),
        zoom: options.zoom ?? 1,
        maxZoom: options.maxZoom ?? undefined,
        minZoom: options.minZoom ?? undefined
      }),
    });

    for (const overlay of markers.overlays) {
      this.map.addOverlay(overlay);
    }

    if (options.markerFitZoom) {
      helper.fitZoomToMarker(this.map, markers.layers, options.markerFitZoomPadding);
    }

    helper.registerMarkerClickHandlers(this.map, markers.overlays, options.markerPopupOnClick);
  }

  public getOpenLayerMap = () => this.map;

  protected makeMarkers = (markers: MarkerOption[]) => {
    const layers: Vector[] = [];
    const overlays: Overlay[] = [];
    markers.forEach(marker => {
      const point = marker.point instanceof Point ? marker.point : new Point(fromLonLat(marker.point));
      const feature = helper.createFeature(point);
      const style = helper.createIconStyle(marker.image);
      if (marker.popup) {
        feature.set(POPUP_ACTIVE, true);
        feature.set(POPUP_CONTENT, marker.popup);
        feature.set(POPUP_ID, 'popup-' + Map.featureIndex++);
      }
      layers.push(helper.createIconVector(style, feature));
      overlays.push(helper.createPopupOverlay(feature))
    });
    return { layers, overlays };
  }

}
